import React, { useCallback, useMemo, useState } from 'react';
import {
  Button,
  IconCalendar,
  IconDownload,
  IconFilledInfo,
  Tag,
} from 'gatorcomponents';
import useLocale from '@/hooks/useLocale';
import {
  Table,
  TablePagination,
  useTableContext,
} from '@/components/Table/Table';
import { useIsMdUp } from '../../../../../components/Table/table.utils';
import { GatorProtectPanel } from '../../GatorProtectHandler.styles';
import * as S from './ThreatsTableSection.styles';
import type { GatorProtectThreatFile } from './model';
import { threatsListMapper } from './mappers';
import { MOCK_THREATS_LIST_RESPONSE_API } from './mock';
import {
  ellipsisThreatPathForMobile,
  formatThreatDateTime,
  resolveThreatSituationTag,
  type ThreatSituationTagLabels,
} from './threatsTableSection.utils';

/**
 * Props for the threats table block. Intentionally empty for now — reserved for future wiring
 * (e.g. Redux-backed data, injectable mapper in tests).
 */
export interface ThreatsTableSectionProps { }

type ThreatsTableColumns = {
  file: string;
  threat: string;
  situation: string;
  detectedAt: string;
};

type GatorProtectThreatsTableLocale = {
  gatorProtect?: {
    summary?: { title?: string };
    threatsTable?: {
      columns?: Partial<ThreatsTableColumns>;
      situationTags?: Partial<ThreatSituationTagLabels>;
      header?: {
        exportButtonAriaLabel?: string;
        infoPlaceholder?: string;
      };
    };
  };
};

type ThreatSituationCellProps = {
  row: GatorProtectThreatFile;
  situationTags: ThreatSituationTagLabels;
};

const ThreatSituationCell = ({ row, situationTags }: ThreatSituationCellProps) => {
  const { label, variant } = resolveThreatSituationTag(row.status, situationTags);
  return (
    <Tag
      label={label}
      variant={variant}
      size="sm"
    />
  );
};

type ThreatsTableLayoutProps = {
  columns: ThreatsTableColumns;
  situationTags: ThreatSituationTagLabels;
};

const ThreatsTableLayout = ({ columns, situationTags }: ThreatsTableLayoutProps) => {
  const { sortedData, isDesktop } = useTableContext<GatorProtectThreatFile>();

  if (!isDesktop) {
    return (
      <S.ThreatsMobileList>
        {sortedData.map(row => (
          <S.ThreatsMobileCard key={row.path}>
            <S.ThreatsMobileField data-column-id="path">
              <S.ThreatsMobileLabel>{`${columns.file}:`}</S.ThreatsMobileLabel>
              <S.ThreatsMobileValue>
                {ellipsisThreatPathForMobile(row.path)}
              </S.ThreatsMobileValue>
            </S.ThreatsMobileField>
            <S.ThreatsMobileField data-column-id="name">
              <S.ThreatsMobileLabel>{`${columns.threat}:`}</S.ThreatsMobileLabel>
              <S.ThreatsMobileValue>{row.name}</S.ThreatsMobileValue>
            </S.ThreatsMobileField>
            <S.ThreatsMobileField data-column-id="status">
              <S.ThreatsMobileLabel>{`${columns.situation}:`}</S.ThreatsMobileLabel>
              <S.ThreatsMobileValue>
                <ThreatSituationCell row={row} situationTags={situationTags} />
              </S.ThreatsMobileValue>
            </S.ThreatsMobileField>
            <S.ThreatsMobileField data-column-id="detectedAt">
              <S.ThreatsMobileLabel>{`${columns.detectedAt}:`}</S.ThreatsMobileLabel>
              <S.ThreatsMobileValue>
                {formatThreatDateTime(row.detectedAt)}
              </S.ThreatsMobileValue>
            </S.ThreatsMobileField>
          </S.ThreatsMobileCard>
        ))}
      </S.ThreatsMobileList>
    );
  }

  return (
    <Table.Scroll>
      <Table.Table>
        <thead>
          <tr>
            <Table.Th>{columns.file}</Table.Th>
            <Table.Th>{columns.threat}</Table.Th>
            <Table.Th>{columns.situation}</Table.Th>
            <Table.Th>{columns.detectedAt}</Table.Th>
          </tr>
        </thead>
        <tbody>
          {sortedData.map(row => (
            <tr key={row.path}>
              <Table.Td>{row.path}</Table.Td>
              <Table.Td>{row.name}</Table.Td>
              <Table.Td>
                <ThreatSituationCell row={row} situationTags={situationTags} />
              </Table.Td>
              <Table.Td>{formatThreatDateTime(row.detectedAt)}</Table.Td>
            </tr>
          ))}
        </tbody>
      </Table.Table>
    </Table.Scroll>
  );
};

const ThreatsTableSection = () => {
  const { gatorProtect } = useLocale() as GatorProtectThreatsTableLocale;
  const isDesktop = useIsMdUp();
  const sectionTitle = gatorProtect?.summary?.title ?? '';
  const rawColumns = gatorProtect?.threatsTable?.columns;
  const columns: ThreatsTableColumns = {
    file: rawColumns?.file ?? '',
    threat: rawColumns?.threat ?? '',
    situation: rawColumns?.situation ?? '',
    detectedAt: rawColumns?.detectedAt ?? '',
  };
  const rawSituationTags = gatorProtect?.threatsTable?.situationTags;
  const situationTags: ThreatSituationTagLabels = {
    quarantine: rawSituationTags?.quarantine ?? '',
    remediated: rawSituationTags?.remediated ?? '',
  };
  const rawHeader = gatorProtect?.threatsTable?.header;
  const exportButtonAriaLabel = rawHeader?.exportButtonAriaLabel ?? '';
  const infoPlaceholder = rawHeader?.infoPlaceholder ?? '';
  const handleExportClick = useCallback(() => {
    // Reserved for future export wiring (Gator Protect threats table).
  }, []);

  const threatsList = useMemo(
    () => threatsListMapper.fromApi(MOCK_THREATS_LIST_RESPONSE_API),
    [],
  );
  const { page: pageMeta, files } = threatsList.data;
  const [page, setPage] = useState(pageMeta.currentPage);

  const tableRows = useMemo(() => {
    if (page !== pageMeta.currentPage) {
      return [];
    }
    return files;
  }, [page, pageMeta.currentPage, files]);

  return (
    <GatorProtectPanel data-testid="threats-table-section">
      <S.ThreatsTableSectionHeader>
        {isDesktop ? (
          <S.ThreatsTableSectionHeaderActions>
            <S.ThreatsTableSectionTitle>{sectionTitle}</S.ThreatsTableSectionTitle>
            <S.ActionsWrapper>
              <Button
                iconLeft={<IconDownload color="highPure" secondaryColor="transparent" />}
                variant="primary"
                size="medium2"
                label={exportButtonAriaLabel}
                onClick={handleExportClick}
              />
              <S.ThreatsTableSectionInfoStrip aria-live="polite">
                <IconCalendar color="lowLight" size="sm" />
                {infoPlaceholder}
              </S.ThreatsTableSectionInfoStrip>
            </S.ActionsWrapper>

          </S.ThreatsTableSectionHeaderActions>
        ) : (
          <>
            <S.ThreatsTableSectionMobileTopRow>
              <S.ThreatsTableSectionMobileTitleGroup>
                <S.ThreatsTableSectionMobileTitle>
                  {sectionTitle}
                  <S.ThreatsTableSectionMobileInfoIcon>
                    <IconFilledInfo color="primaryBlue" size="sm" />
                  </S.ThreatsTableSectionMobileInfoIcon>
                </S.ThreatsTableSectionMobileTitle>
              </S.ThreatsTableSectionMobileTitleGroup>
              <Button
                iconLeft={<IconDownload color="primaryBlue" />}
                variant="secondary"
                notFillSVG
                size="small"
                label=""
                onClick={handleExportClick}
              />
            </S.ThreatsTableSectionMobileTopRow>
            <S.ThreatsTableSectionInfoStrip aria-live="polite">
              <IconCalendar color="lowLight" size="sm" />
              {infoPlaceholder}
            </S.ThreatsTableSectionInfoStrip>
          </>
        )}

      </S.ThreatsTableSectionHeader>
      <Table.Provider<GatorProtectThreatFile>
        data={tableRows}
        data-testid="gator-protect-threats-table"
      >
        <ThreatsTableLayout columns={columns} situationTags={situationTags} />
      </Table.Provider>
      <TablePagination
        mode="local"
        page={page}
        pageSize={pageMeta.perPage}
        totalItems={pageMeta.total}
        onPageChange={setPage}
      />
    </GatorProtectPanel>
  );
};

export default ThreatsTableSection;
